import React, { useState, useEffect } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { CheckCircle, XCircle, Download, Eye, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

const BUSINESS_TYPE_LABELS = {
  travel_agency: 'Travel Agency',
  tour_operator: 'Tour Operator',
  dmc: 'DMC',
  travel_advisor: 'Travel Advisor',
  wholesaler: 'Wholesaler',
  consortium_network: 'Consortium / Travel Network',
  ota_platform: 'OTA Platform',
  destination_specialist: 'Destination Specialist',
  scheduled_tour_operator: 'Scheduled Tour Operator',
  package_tour_supplier: 'Package Tour Supplier',
  hotel_resort: 'Hotel / Resort',
  villa_accommodation: 'Villa / Accommodation Provider',
  attraction_experience: 'Attraction / Experience Provider',
  bus_coach_operator: 'Bus / Coach Operator',
  car_rental: 'Car Rental',
  transfer_transportation: 'Transfer / Transportation Provider',
  rail_cruise_transport: 'Rail / Cruise / Other Transport',
  airport_services: 'Airport Services',
  travel_insurance: 'Travel Insurance Provider',
  visa_immigration: 'Visa / Immigration Services',
  tourism_board: 'Tourism Board / Tourism Authority',
  dmo: 'Destination Marketing Organization (DMO)',
  cvb: 'Convention & Visitors Bureau (CVB)',
  city_regional_tourism: 'City / Regional Tourism Organization',
  national_tourism: 'National Tourism Organization',
  tourism_association: 'Tourism Association',
  destination_promotion: 'Destination Promotion Organization',
  destination_organization: 'Other Destination Organization',
  other_travel_service: 'Other Travel Service',
  other: 'Other'
};

const ROLE_LABELS = {
  supplier: 'Supplier',
  distributor: 'Distributor',
  destination_partner: 'Destination Partner',
  other: 'Other'
};

export const AdminB2BDashboard = () => {
  const [partners, setPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [expandedPartner, setExpandedPartner] = useState(null);
  
  // Filters
  const [roleFilter, setRoleFilter] = useState('all');
  const [businessTypeFilter, setBusinessTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const loadPartners = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/b2b/admin/all`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPartners(data.partners);
        setFilteredPartners(data.partners);
      } else {
        console.error('Failed to load partners');
      }
    } catch (err) {
      console.error('Failed to load partners:', err);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  useEffect(() => {
    let filtered = partners;
    
    if (roleFilter !== 'all') {
      filtered = filtered.filter(p => 
        p.partner_role && p.partner_role.includes(roleFilter)
      );
    }
    
    if (businessTypeFilter !== 'all') {
      filtered = filtered.filter(p => p.business_type === businessTypeFilter);
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.company_name?.toLowerCase().includes(query) ||
        p.contact_person?.toLowerCase().includes(query) ||
        p.business_email?.toLowerCase().includes(query) ||
        p.partner_id?.toLowerCase().includes(query)
      );
    }
    
    setFilteredPartners(filtered);
  }, [roleFilter, businessTypeFilter, statusFilter, searchQuery, partners]);

  const handleApprove = async (partnerId) => {
    if (!window.confirm('Approve this partner? They will receive login credentials via email.')) {
      return;
    }

    setLoading(true);
    setMessage('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/b2b/admin/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          partner_id: partnerId,
          action: 'approve'
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setMessage(`✅ Partner approved! Credentials sent to their email.`);
        loadPartners();
      } else {
        setMessage(`❌ Error: ${data.detail}`);
      }
    } catch (err) {
      setMessage(`❌ Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (partnerId) => {
    const reason = window.prompt('Enter rejection reason:');
    
    if (!reason || !reason.trim()) {
      alert('Rejection reason is required');
      return;
    }

    if (!window.confirm('Reject this B2B partner application?')) return;

    setLoading(true);
    setMessage('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/b2b/admin/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          partner_id: partnerId,
          action: 'reject',
          rejection_reason: reason.trim()
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setMessage(`✅ Partner application rejected.`);
        loadPartners();
      } else {
        setMessage(`❌ Error: ${data.detail}`);
      }
    } catch (err) {
      setMessage(`❌ Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDocument = async (partnerId, docIndex, filename) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/b2b/admin/document/${partnerId}/${docIndex}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `document_${docIndex}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download document');
      }
    } catch (err) {
      alert(`Download error: ${err.message}`);
    }
  };

  const handleViewDocument = async (partnerId, docIndex) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/b2b/admin/document/${partnerId}/${docIndex}`;
    window.open(url, '_blank');
  };

  const toggleExpand = (partnerId) => {
    setExpandedPartner(expandedPartner === partnerId ? null : partnerId);
  };

  // Load partners on mount
  useEffect(() => {
    loadPartners();
  }, []);

  return (
    <div>
      <Navigation />
      
      <section className="section-padding" style={{ background: 'var(--bg-secondary)', minHeight: '100vh' }}>
        <div className="container" style={{ maxWidth: '1400px' }}>
          <h1 className="hero-large" style={{ marginBottom: '32px' }}>
            B2B Partner Applications
          </h1>

          {message && (
            <div style={{
              padding: '16px',
              marginBottom: '24px',
              background: message.includes('✅') ? '#d4edda' : '#f8d7da',
              border: message.includes('✅') ? '1px solid #28a745' : '1px solid #dc3545',
              borderRadius: '4px'
            }}>
              <p style={{ margin: 0, color: message.includes('✅') ? '#155724' : '#721c24' }}>{message}</p>
            </div>
          )}

          {/* Filters */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
            padding: '20px',
            background: '#fff',
            border: '1px solid #ddd'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500' }}>
                Partner Role
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="all">All Roles</option>
                <option value="supplier">Supplier</option>
                <option value="distributor">Distributor</option>
                <option value="destination_partner">Destination Partner</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500' }}>
                Business Type
              </label>
              <select
                value={businessTypeFilter}
                onChange={(e) => setBusinessTypeFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="all">All Types</option>
                <optgroup label="Travel Trade">
                  <option value="travel_agency">Travel Agency</option>
                  <option value="wholesaler">Wholesaler</option>
                  <option value="consortium_network">Consortium</option>
                </optgroup>
                <optgroup label="Suppliers">
                  <option value="tour_operator">Tour Operator</option>
                  <option value="dmc">DMC</option>
                  <option value="hotel_resort">Hotel/Resort</option>
                </optgroup>
                <optgroup label="Destination Partners">
                  <option value="tourism_board">Tourism Board</option>
                  <option value="dmo">DMO</option>
                  <option value="cvb">CVB</option>
                </optgroup>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500' }}>
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="all">All Status</option>
                <option value="pending_approval">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500' }}>
                Search
              </label>
              <input
                type="text"
                placeholder="Company name, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {/* Partners List */}
          <div style={{ fontSize: '14px', marginBottom: '16px', color: '#666' }}>
            Showing {filteredPartners.length} of {partners.length} partners
          </div>

          {loading && <p>Loading...</p>}
          
          {!loading && filteredPartners.length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center', background: '#fff', border: '1px solid #ddd' }}>
              <AlertCircle size={48} style={{ color: '#999', marginBottom: '16px' }} />
              <p style={{ color: '#666' }}>No partners found matching your filters</p>
            </div>
          )}

          {!loading && filteredPartners.map((partner) => {
            const isExpanded = expandedPartner === partner.partner_id;
            
            return (
              <div
                key={partner.partner_id}
                style={{
                  marginBottom: '16px',
                  background: '#fff',
                  border: '1px solid #ddd',
                  overflow: 'hidden'
                }}
              >
                {/* Header */}
                <div
                  onClick={() => toggleExpand(partner.partner_id)}
                  style={{
                    padding: '20px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: isExpanded ? '1px solid #ddd' : 'none'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
                        {partner.company_name}
                      </h3>
                      <span style={{
                        padding: '4px 12px',
                        background: partner.status === 'approved' ? '#28a745' : '#ffc107',
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        borderRadius: '12px'
                      }}>
                        {partner.status}
                      </span>
                      {partner.is_government_entity && (
                        <span style={{
                          padding: '4px 12px',
                          background: '#007bff',
                          color: '#fff',
                          fontSize: '11px',
                          borderRadius: '12px'
                        }}>
                          GOV
                        </span>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '14px', color: '#666' }}>
                      <span><strong>ID:</strong> {partner.partner_id}</span>
                      <span><strong>Type:</strong> {BUSINESS_TYPE_LABELS[partner.business_type] || partner.business_type || 'N/A'}</span>
                      {partner.partner_role && partner.partner_role.length > 0 && (
                        <span>
                          <strong>Roles:</strong> {partner.partner_role.map(r => ROLE_LABELS[r] || r).join(', ')}
                        </span>
                      )}
                      <span>{partner.city}, {partner.country}</span>
                    </div>
                  </div>
                  
                  {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div style={{ padding: '20px', background: '#f9f9f9' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                      
                      {/* Identity */}
                      <div>
                        <h4 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', textTransform: 'uppercase', color: '#B8860B' }}>
                          Identity
                        </h4>
                        <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                          <div><strong>Partner ID:</strong> {partner.partner_id}</div>
                          <div><strong>Company:</strong> {partner.company_name}</div>
                          <div><strong>Contact:</strong> {partner.contact_person}</div>
                          <div><strong>Job Title:</strong> {partner.designation || 'N/A'}</div>
                          <div><strong>Email:</strong> {partner.business_email}</div>
                          <div><strong>Phone:</strong> {partner.phone}</div>
                          {partner.whatsapp && <div><strong>WhatsApp:</strong> {partner.whatsapp}</div>}
                          {partner.website && <div><strong>Website:</strong> <a href={partner.website} target="_blank" rel="noopener noreferrer">{partner.website}</a></div>}
                          <div><strong>Location:</strong> {partner.city}, {partner.country}</div>
                        </div>
                      </div>

                      {/* Classification */}
                      <div>
                        <h4 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', textTransform: 'uppercase', color: '#B8860B' }}>
                          Classification
                        </h4>
                        <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                          <div><strong>Partner Role(s):</strong></div>
                          {partner.partner_role && partner.partner_role.length > 0 ? (
                            <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                              {partner.partner_role.map((role, idx) => (
                                <li key={idx}>{ROLE_LABELS[role] || role}</li>
                              ))}
                            </ul>
                          ) : (
                            <div style={{ color: '#999' }}>Not specified</div>
                          )}
                          <div style={{ marginTop: '8px' }}><strong>Business Type:</strong> {BUSINESS_TYPE_LABELS[partner.business_type] || partner.business_type || 'N/A'}</div>
                          {partner.is_government_entity && <div><strong>Government Entity:</strong> Yes</div>}
                          {partner.membership_organization && <div><strong>Membership Org:</strong> Yes</div>}
                        </div>
                      </div>

                      {/* Market/Distribution */}
                      {((partner.partner_role && (partner.partner_role.includes('supplier') || partner.partner_role.includes('distributor'))) ||
                        (partner.target_markets && partner.target_markets.length > 0) ||
                        (partner.distribution_channels && partner.distribution_channels.length > 0)) && (
                        <div>
                          <h4 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', textTransform: 'uppercase', color: '#B8860B' }}>
                            Market & Distribution
                          </h4>
                          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                            {partner.target_markets && partner.target_markets.length > 0 && (
                              <>
                                <div><strong>Target Markets:</strong></div>
                                <div style={{ marginTop: '4px', color: '#666' }}>
                                  {partner.target_markets.map(m => m.replace('_', ' ').toUpperCase()).join(', ')}
                                </div>
                              </>
                            )}
                            {partner.distribution_channels && partner.distribution_channels.length > 0 && (
                              <>
                                <div style={{ marginTop: '8px' }}><strong>Distribution Channels:</strong></div>
                                <div style={{ marginTop: '4px', color: '#666' }}>
                                  {partner.distribution_channels.map(c => c.toUpperCase()).join(', ')}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Supplier/Product Profile */}
                      {(partner.services_offered && partner.services_offered.length > 0) || partner.destination_coverage && (
                        <div>
                          <h4 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', textTransform: 'uppercase', color: '#B8860B' }}>
                            Supplier Profile
                          </h4>
                          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                            {partner.services_offered && partner.services_offered.length > 0 && (
                              <>
                                <div><strong>Services Offered:</strong></div>
                                <div style={{ marginTop: '4px', color: '#666' }}>
                                  {partner.services_offered.map(s => s.replace('_', ' ')).join(', ')}
                                </div>
                              </>
                            )}
                            {partner.destination_coverage && Array.isArray(partner.destination_coverage) && partner.destination_coverage.length > 0 && (
                              <>
                                <div style={{ marginTop: '8px' }}><strong>Destination Coverage:</strong></div>
                                <div style={{ marginTop: '4px', color: '#666' }}>
                                  {partner.destination_coverage.join(', ')}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Destination Partner Profile */}
                      {(partner.partner_role && partner.partner_role.includes('destination_partner')) && (
                        <div>
                          <h4 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', textTransform: 'uppercase', color: '#B8860B' }}>
                            Destination Partner Profile
                          </h4>
                          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                            {partner.represented_destinations && Array.isArray(partner.represented_destinations) && partner.represented_destinations.length > 0 && (
                              <>
                                <div><strong>Represented Destinations:</strong></div>
                                <div style={{ marginTop: '4px', color: '#666' }}>
                                  {partner.represented_destinations.join(', ')}
                                </div>
                              </>
                            )}
                            {partner.destination_capabilities && partner.destination_capabilities.length > 0 && (
                              <>
                                <div style={{ marginTop: '8px' }}><strong>Capabilities:</strong></div>
                                <div style={{ marginTop: '4px', color: '#666' }}>
                                  {partner.destination_capabilities.map(c => c.replace('_', ' ')).join(', ')}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Compliance */}
                      <div>
                        <h4 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', textTransform: 'uppercase', color: '#B8860B' }}>
                          Compliance
                        </h4>
                        <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                          {partner.regulatory_info && (
                            <>
                              {partner.regulatory_info.license_type && (
                                <div><strong>License Type:</strong> {partner.regulatory_info.license_type}</div>
                              )}
                              {partner.regulatory_info.license_number && (
                                <div><strong>License #:</strong> {partner.regulatory_info.license_number}</div>
                              )}
                              {partner.regulatory_info.issuing_authority && (
                                <div><strong>Authority:</strong> {partner.regulatory_info.issuing_authority}</div>
                              )}
                            </>
                          )}
                          {partner.documents && partner.documents.length > 0 && (
                            <>
                              <div style={{ marginTop: '8px' }}><strong>Documents ({partner.documents.length}):</strong></div>
                              {partner.documents.map((doc, idx) => (
                                <div key={idx} style={{ 
                                  marginTop: '8px',
                                  padding: '8px',
                                  background: '#f5f5f5',
                                  borderRadius: '4px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}>
                                  <span style={{ fontSize: '14px' }}>
                                    📄 {doc.filename || `Document ${idx + 1}`}
                                  </span>
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                      onClick={() => handleViewDocument(partner.partner_id, idx)}
                                      style={{
                                        padding: '6px 12px',
                                        background: '#0066cc',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                    >
                                      <Eye size={14} />
                                      View
                                    </button>
                                    <button
                                      onClick={() => handleDownloadDocument(partner.partner_id, idx, doc.filename)}
                                      style={{
                                        padding: '6px 12px',
                                        background: '#28a745',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                    >
                                      <Download size={14} />
                                      Download
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Application Info */}
                      <div>
                        <h4 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', textTransform: 'uppercase', color: '#B8860B' }}>
                          Application
                        </h4>
                        <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                          <div><strong>Status:</strong> {partner.status}</div>
                          <div><strong>Submitted:</strong> {new Date(partner.created_at).toLocaleDateString()}</div>
                          {partner.approved_at && (
                            <div><strong>Approved:</strong> {new Date(partner.approved_at).toLocaleDateString()}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    {partner.status === 'pending_approval' && (
                      <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
                        <button
                          onClick={() => handleApprove(partner.partner_id)}
                          disabled={loading}
                          style={{
                            padding: '12px 24px',
                            background: '#28a745',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.6 : 1
                          }}
                        >
                          <CheckCircle size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                          Approve Partner
                        </button>
                        <button
                          onClick={() => handleReject(partner.partner_id)}
                          disabled={loading}
                          style={{
                            padding: '12px 24px',
                            background: '#dc3545',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.6 : 1
                          }}
                        >
                          <XCircle size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                          Reject
                        </button>
                      </div>
                    )}
                    
                    {/* Show rejection reason if rejected */}
                    {partner.status === 'rejected' && partner.rejection_reason && (
                      <div style={{ 
                        marginTop: '16px',
                        padding: '12px',
                        background: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: '4px'
                      }}>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
                          Rejection Reason:
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          {partner.rejection_reason}
                        </div>
                        {partner.rejected_at && (
                          <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                            Rejected on {new Date(partner.rejected_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
      
      <Footer />
    </div>
  );
};
